import { Button } from "@groot/ui/button";
import { PageHeader } from "@groot/shell/components/layout";
import { Page } from "@/app/shared/components/page";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { useDetailPage } from "@/hooks";
import { formatDetailedDateTime } from "@/lib/utils";
import { apiClient } from "@/services/api";
import { json } from "@codemirror/lang-json";
import CodeMirror from "@uiw/react-codemirror";
import { ArrowLeft, CheckCircle, ExternalLink, Trash2 } from "lucide-react";
import { useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

export function TransactionalSmsDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const fetchFn = useCallback(
    (id: string) => apiClient.getTransactionalSmsById(Number.parseInt(id, 10)),
    [],
  );

  const deleteFn = useCallback(
    (id: string) => apiClient.deleteTransactionalSms(Number.parseInt(id, 10)),
    [],
  );

  const {
    data: sms,
    loading,
    error,
    handleDelete,
  } = useDetailPage({
    fetchFn,
    deleteFn,
    id,
    redirectPath: "/transactional-sms",
    resourceName: "SMS",
  });

  if (loading) {
    return (
      <Page maxWidth="5xl">
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (error || !sms) {
    return (
      <Page maxWidth="5xl">
        <Button
          variant="ghost"
          onClick={() => navigate("/transactional-sms")}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to SMS
        </Button>
        <div className="text-center py-20">
          <p className="text-muted-foreground">{error || "SMS not found"}</p>
        </div>
      </Page>
    );
  }

  return (
    <Page maxWidth="5xl">
      {/* Header */}
      <Button variant="ghost" onClick={() => navigate("/transactional-sms")} className="mb-4 -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to SMS
      </Button>
      <PageHeader
        title={`SMS #${sms.id}`}
        description={`Received ${formatDetailedDateTime(sms.date)}`}
        actions={
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="size-4 mr-2" />
            Delete
          </Button>
        }
        className="mb-8"
      />

      <div className="space-y-8">
        {/* Details Section */}
        <div>
          <h2 className="text-sm font-medium text-foreground mb-4">Details</h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-sm text-muted-foreground">From</dt>
              <dd className="text-sm text-foreground mt-1">{sms.from || "Unknown"}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Timestamp</dt>
              <dd className="text-sm text-foreground font-mono mt-1">{sms.timestamp}</dd>
            </div>
            {sms.location && (
              <div className="md:col-span-2">
                <dt className="text-sm text-muted-foreground">Location</dt>
                <dd className="text-sm text-foreground font-mono mt-1">
                  {JSON.stringify(sms.location)}
                </dd>
              </div>
            )}
            <div className="md:col-span-2">
              <dt className="text-sm text-muted-foreground">Status</dt>
              <dd className="mt-1">
                {sms.Transaction && sms.Transaction.length > 0 ? (
                  <span className="text-sm text-success">
                    <CheckCircle className="size-3 mr-1 inline" />
                    {sms.Transaction.length} transaction
                    {sms.Transaction.length > 1 ? "s" : ""} created
                  </span>
                ) : sms.extractedOutput ? (
                  <span className="text-sm text-muted-foreground">Parsed, no transaction</span>
                ) : (
                  <span className="text-sm text-muted-foreground/70">Not parsed</span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Message Body */}
        <div className="pt-6 border-t border-border">
          <h2 className="text-sm font-medium text-foreground mb-4">Message</h2>
          <div className="p-4 bg-muted rounded-lg border border-border">
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {sms.body || "No message body"}
            </p>
          </div>
        </div>

        {/* Extracted Output */}
        {sms.extractedOutput && (
          <div className="pt-6 border-t border-border">
            <h2 className="text-sm font-medium text-foreground mb-4">Extracted Data</h2>
            <div className="border border-border rounded-lg overflow-hidden">
              <CodeMirror
                value={JSON.stringify(sms.extractedOutput, null, 2)}
                extensions={[json()]}
                editable={false}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: true,
                  highlightActiveLine: false,
                }}
              />
            </div>
          </div>
        )}

        {/* Related Transactions */}
        {sms.Transaction && sms.Transaction.length > 0 && (
          <div className="pt-6 border-t border-border">
            <h2 className="text-sm font-medium text-foreground mb-4">
              Related Transactions ({sms.Transaction.length})
            </h2>
            <div className="border border-border rounded-lg divide-y divide-border">
              {sms.Transaction.map((txn) => (
                <div key={txn.id} className="p-4 flex items-center justify-between hover:bg-muted">
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      ₹{txn.amount.toFixed(2)}
                      <span className="text-xs text-muted-foreground ml-2">{txn.type}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {txn.description || "No description"}
                    </div>
                  </div>
                  <Button asChild variant="ghost" size="sm">
                    <Link to={`/transactions?id=${txn.id}`}>
                      <ExternalLink className="size-4 mr-2" />
                      View
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="pt-6 border-t border-border">
          <h2 className="text-sm font-medium text-foreground mb-4">Metadata</h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-sm text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground mt-1">
                {formatDetailedDateTime(sms.createdAt)}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Updated At</dt>
              <dd className="text-sm text-foreground mt-1">
                {formatDetailedDateTime(sms.updatedAt)}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </Page>
  );
}
